/**
 * Step 3: Sports Activities - Detailed sport tracking with AI calorie calculation
 * 
 * Users can add specific sports with duration and frequency per week.
 * The AI calculates precise calorie burn for each activity.
 */
import React, { useState, useMemo } from 'react';
import { Plus, X, Dumbbell, Clock, Calendar, Flame, Sparkles, ChevronDown, Footprints, Bike, Waves, Activity, Sailboat, Timer, Zap, PersonStanding, Music, Hand, CircleDot, Volleyball, Target, Flag, Mountain, MountainSnow, Snowflake, Medal, Lightbulb } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

// Predefined sports with MET values (Metabolic Equivalent of Task)
// MET x weight(kg) x duration(hours) = calories burned
// Intensity multipliers: leicht=0.75, mittel=1.0, intensiv=1.35
const INTENSITY_LEVELS = [
  { id: 'leicht', label: 'Leicht', multiplier: 0.75, color: 'text-green-400 bg-green-500/20 border-green-500/40' },
  { id: 'mittel', label: 'Mittel', multiplier: 1.0, color: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/40' },
  { id: 'intensiv', label: 'Intensiv', multiplier: 1.35, color: 'text-red-400 bg-red-500/20 border-red-500/40' },
];

const SPORT_DATABASE = [
  // Ausdauer
  { id: 'joggen', name: 'Joggen', category: 'Ausdauer', met: 7.0, icon: Footprints, defaultMinutes: 30 },
  { id: 'laufen_schnell', name: 'Laufen (schnell)', category: 'Ausdauer', met: 9.8, icon: Footprints, defaultMinutes: 30 },
  { id: 'radfahren', name: 'Radfahren', category: 'Ausdauer', met: 6.0, icon: Bike, defaultMinutes: 45 },
  { id: 'radfahren_intensiv', name: 'Radfahren (intensiv)', category: 'Ausdauer', met: 10.0, icon: Bike, defaultMinutes: 45 },
  { id: 'schwimmen', name: 'Schwimmen', category: 'Ausdauer', met: 6.0, icon: Waves, defaultMinutes: 45 },
  { id: 'schwimmen_intensiv', name: 'Schwimmen (intensiv)', category: 'Ausdauer', met: 9.8, icon: Waves, defaultMinutes: 45 },
  { id: 'walken', name: 'Walken / Nordic Walking', category: 'Ausdauer', met: 4.5, icon: Footprints, defaultMinutes: 45 },
  { id: 'crosstrainer', name: 'Crosstrainer', category: 'Ausdauer', met: 5.0, icon: Activity, defaultMinutes: 30 },
  { id: 'rudern', name: 'Rudern / Rudergerät', category: 'Ausdauer', met: 7.0, icon: Sailboat, defaultMinutes: 30 },
  { id: 'seilspringen', name: 'Seilspringen', category: 'Ausdauer', met: 11.0, icon: Timer, defaultMinutes: 15 },
  
  // Kraft
  { id: 'krafttraining', name: 'Krafttraining', category: 'Kraft', met: 5.0, icon: Dumbbell, defaultMinutes: 60 },
  { id: 'krafttraining_intensiv', name: 'Krafttraining (intensiv)', category: 'Kraft', met: 6.0, icon: Dumbbell, defaultMinutes: 60 },
  { id: 'ems_training', name: 'EMS Training', category: 'Kraft', met: 6.5, icon: Zap, defaultMinutes: 20 },
  { id: 'bodyweight', name: 'Bodyweight / Calisthenics', category: 'Kraft', met: 5.0, icon: PersonStanding, defaultMinutes: 45 },
  { id: 'crossfit', name: 'CrossFit / HIIT', category: 'Kraft', met: 8.0, icon: Flame, defaultMinutes: 45 },
  
  // Kurse & Gruppenfitness
  { id: 'yoga', name: 'Yoga', category: 'Kurse', met: 3.0, icon: PersonStanding, defaultMinutes: 60 },
  { id: 'pilates', name: 'Pilates', category: 'Kurse', met: 3.5, icon: PersonStanding, defaultMinutes: 60 },
  { id: 'spinning', name: 'Spinning / Indoor Cycling', category: 'Kurse', met: 8.5, icon: Bike, defaultMinutes: 45 },
  { id: 'aerobic', name: 'Aerobic / Step', category: 'Kurse', met: 6.5, icon: Activity, defaultMinutes: 45 },
  { id: 'zumba', name: 'Zumba / Tanz-Fitness', category: 'Kurse', met: 6.0, icon: Music, defaultMinutes: 45 },
  { id: 'boxing', name: 'Boxen / Kickboxen', category: 'Kurse', met: 7.8, icon: Hand, defaultMinutes: 60 },
  
  // Ballsport
  { id: 'fussball', name: 'Fussball', category: 'Ballsport', met: 7.0, icon: CircleDot, defaultMinutes: 90 },
  { id: 'basketball', name: 'Basketball', category: 'Ballsport', met: 6.5, icon: CircleDot, defaultMinutes: 60 },
  { id: 'volleyball', name: 'Volleyball', category: 'Ballsport', met: 4.0, icon: Volleyball, defaultMinutes: 60 },
  { id: 'tennis', name: 'Tennis', category: 'Ballsport', met: 7.3, icon: Target, defaultMinutes: 60 },
  { id: 'badminton', name: 'Badminton', category: 'Ballsport', met: 5.5, icon: Target, defaultMinutes: 60 },
  { id: 'squash', name: 'Squash', category: 'Ballsport', met: 7.3, icon: Target, defaultMinutes: 45 },
  { id: 'golf', name: 'Golf', category: 'Ballsport', met: 3.5, icon: Flag, defaultMinutes: 120 },
  
  // Outdoor
  { id: 'wandern', name: 'Wandern', category: 'Outdoor', met: 5.3, icon: Mountain, defaultMinutes: 120 },
  { id: 'bergsteigen', name: 'Bergsteigen / Klettern', category: 'Outdoor', met: 8.0, icon: MountainSnow, defaultMinutes: 120 },
  { id: 'ski_alpin', name: 'Ski Alpin', category: 'Outdoor', met: 5.0, icon: Snowflake, defaultMinutes: 180 },
  { id: 'ski_langlauf', name: 'Ski Langlauf', category: 'Outdoor', met: 9.0, icon: Snowflake, defaultMinutes: 60 },
  { id: 'eislaufen', name: 'Eislaufen', category: 'Outdoor', met: 5.5, icon: Snowflake, defaultMinutes: 60 },
  
  // Sonstiges
  { id: 'sonstiges', name: 'Sonstiges', category: 'Sonstiges', met: 5.0, icon: Medal, defaultMinutes: 45 },
];

// Resolve the Lucide icon component for a given sport id (fallback: Medal)
const getSportIcon = (id) => SPORT_DATABASE.find(s => s.id === id)?.icon || Medal;

// Get unique categories
const CATEGORIES = [...new Set(SPORT_DATABASE.map(s => s.category))];

const StepSportsActivities = ({ prefs, setPrefs }) => {
  const [showAddSport, setShowAddSport] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Ausdauer');
  const [customSportName, setCustomSportName] = useState('');
  
  // Get current sports from prefs
  const sports = prefs.sports_activities || [];
  
  // Calculate total weekly calories burned
  const weeklyStats = useMemo(() => {
    const weight = prefs.weight_kg || 70; // Default weight if not set
    
    let totalCalories = 0;
    let totalMinutes = 0;
    let totalSessions = 0;
    
    sports.forEach(sport => {
      const sportData = SPORT_DATABASE.find(s => s.id === sport.id) || { met: 5.0 };
      const intensityLevel = INTENSITY_LEVELS.find(l => l.id === (sport.intensity || 'mittel'));
      const intensityMultiplier = intensityLevel?.multiplier || 1.0;
      const adjustedMet = sportData.met * intensityMultiplier;
      const caloriesPerSession = adjustedMet * weight * (sport.duration / 60);
      totalCalories += caloriesPerSession * sport.frequency;
      totalMinutes += sport.duration * sport.frequency;
      totalSessions += sport.frequency;
    });
    
    return {
      calories: Math.round(totalCalories),
      minutes: totalMinutes,
      sessions: totalSessions,
      dailyAverage: Math.round(totalCalories / 7)
    };
  }, [sports, prefs.weight_kg]);
  
  // Add a sport
  const addSport = (sportData) => {
    const newSport = {
      id: sportData.id,
      name: sportData.name,
      duration: sportData.defaultMinutes,
      frequency: 2, // Default 2x per week
      met: sportData.met,
      intensity: 'mittel' // Default: mittel (1.0x)
    };
    
    setPrefs(prev => ({
      ...prev,
      sports_activities: [...(prev.sports_activities || []), newSport]
    }));
    setShowAddSport(false);
  };
  
  // Add custom sport
  const addCustomSport = () => {
    if (!customSportName.trim()) return;
    
    const newSport = {
      id: `custom_${Date.now()}`,
      name: customSportName.trim(),
      duration: 45,
      frequency: 2,
      met: 5.0, // Default MET for unknown activities
      intensity: 'mittel'
    };
    
    setPrefs(prev => ({
      ...prev,
      sports_activities: [...(prev.sports_activities || []), newSport]
    }));
    setCustomSportName('');
    setShowAddSport(false);
  };
  
  // Update sport
  const updateSport = (index, field, value) => {
    setPrefs(prev => {
      const updated = [...(prev.sports_activities || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, sports_activities: updated };
    });
  };
  
  // Remove sport
  const removeSport = (index) => {
    setPrefs(prev => ({
      ...prev,
      sports_activities: prev.sports_activities.filter((_, i) => i !== index)
    }));
  };
  
  // Calculate calories for a single sport (with intensity)
  const calculateCalories = (sport) => {
    const weight = prefs.weight_kg || 70;
    const sportData = SPORT_DATABASE.find(s => s.id === sport.id) || { met: sport.met || 5.0 };
    const intensityLevel = INTENSITY_LEVELS.find(l => l.id === (sport.intensity || 'mittel'));
    const intensityMultiplier = intensityLevel?.multiplier || 1.0;
    const adjustedMet = sportData.met * intensityMultiplier;
    const caloriesPerSession = adjustedMet * weight * (sport.duration / 60);
    return Math.round(caloriesPerSession);
  };

  return (
    <div className="space-y-4" data-testid="onboarding-step-sports">
      <div className="text-center mb-6">
        <Dumbbell className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-white">Deine Sportarten</h3>
        <p className="text-zinc-400 text-sm">Welche Sportarten machst du regelmässig?</p>
      </div>
      
      {/* Weekly Summary */}
      {sports.length > 0 && (
        <div className="p-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-green-400" />
            <span className="font-semibold text-green-400">KI-Berechnung</span>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-2xl font-bold text-white">{weeklyStats.sessions}x</p>
              <p className="text-xs text-zinc-400">pro Woche</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{weeklyStats.minutes}</p>
              <p className="text-xs text-zinc-400">Minuten/Woche</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-400">{weeklyStats.calories}</p>
              <p className="text-xs text-zinc-400">kcal/Woche</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-green-500/30 text-center">
            <p className="text-sm text-zinc-300">
              ≈ <span className="text-orange-400 font-bold">+{weeklyStats.dailyAverage} kcal</span> täglich durch Sport
            </p>
          </div>
        </div>
      )}
      
      {/* Current Sports List */}
      <div className="space-y-2">
        {sports.map((sport, index) => {
          const SportIcon = getSportIcon(sport.id);
          return (
          <div 
            key={sport.id + index}
            className="p-3 bg-zinc-800/80 rounded-xl border border-zinc-700"
          >
            {/* Header with sport name and remove button */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <SportIcon className="w-5 h-5 text-green-400 shrink-0" />
                <div>
                  <p className="font-medium text-white text-sm">{sport.name}</p>
                  <p className="text-xs text-orange-400">{calculateCalories(sport)} kcal/Einheit</p>
                </div>
              </div>
              <button
                onClick={() => removeSport(index)}
                className="p-1 rounded-full hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Intensity selector */}
            <div className="flex gap-1 mb-2">
              {INTENSITY_LEVELS.map(level => (
                <button
                  key={level.id}
                  onClick={() => updateSport(index, 'intensity', level.id)}
                  className={`flex-1 text-xs py-1 px-2 rounded-md border transition-all ${
                    (sport.intensity || 'mittel') === level.id
                      ? level.color + ' font-medium'
                      : 'text-zinc-500 bg-zinc-900/50 border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  {level.label}
                </button>
              ))}
            </div>
            
            {/* Compact row for duration, frequency and total */}
            <div className="flex items-center gap-2 text-sm">
              {/* Duration */}
              <div className="flex items-center gap-1 bg-zinc-900 rounded-lg px-2 py-1">
                <Clock className="w-3 h-3 text-zinc-500" />
                <Input
                  type="number"
                  min="5"
                  max="300"
                  step="5"
                  value={sport.duration}
                  onChange={(e) => updateSport(index, 'duration', parseInt(e.target.value) || 30)}
                  className="bg-transparent border-0 text-white h-6 w-12 text-center p-0 text-sm"
                />
                <span className="text-zinc-500 text-xs">Min</span>
              </div>
              
              <span className="text-zinc-600">×</span>
              
              {/* Frequency */}
              <div className="flex items-center gap-1 bg-zinc-900 rounded-lg px-2 py-1">
                <Calendar className="w-3 h-3 text-zinc-500" />
                <Input
                  type="number"
                  min="1"
                  max="14"
                  value={sport.frequency}
                  onChange={(e) => updateSport(index, 'frequency', parseInt(e.target.value) || 1)}
                  className="bg-transparent border-0 text-white h-6 w-8 text-center p-0 text-sm"
                />
                <span className="text-zinc-500 text-xs">/Wo</span>
              </div>
              
              <span className="text-zinc-600">=</span>
              
              {/* Weekly total */}
              <span className="text-green-400 font-medium">
                {calculateCalories(sport) * sport.frequency} kcal
              </span>
            </div>
          </div>
          );
        })}
      </div>
      
      {/* Add Sport Button / Panel */}
      {!showAddSport ? (
        <Button
          onClick={() => setShowAddSport(true)}
          variant="outline"
          className="w-full border-dashed border-2 border-zinc-600 hover:border-green-500 bg-transparent hover:bg-green-500/10 text-zinc-400 hover:text-green-400 h-12"
        >
          <Plus className="w-5 h-5 mr-2" />
          Sportart hinzufügen
        </Button>
      ) : (
        <div className="p-3 bg-zinc-800/80 rounded-xl border border-zinc-700 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-white text-sm">Sportart wählen</h4>
            <button
              onClick={() => setShowAddSport(false)}
              className="p-1 rounded-full hover:bg-zinc-700 text-zinc-400"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Category Tabs - Scrollable */}
          <div className="flex gap-1.5 flex-wrap">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                  selectedCategory === cat
                    ? 'bg-green-500 text-black font-medium'
                    : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          
          {/* Sports List - Single column for better fit */}
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {SPORT_DATABASE
              .filter(s => s.category === selectedCategory)
              .filter(s => !sports.find(existing => existing.id === s.id))
              .map(sport => {
                const SportIcon = sport.icon || Medal;
                return (
                <button
                  key={sport.id}
                  onClick={() => addSport(sport)}
                  className="w-full p-2 bg-zinc-900 hover:bg-zinc-700 rounded-lg text-left transition-colors border border-zinc-700 hover:border-green-500 flex items-center gap-2"
                >
                  <SportIcon className="w-4 h-4 text-green-400 shrink-0" />
                  <span className="text-sm text-white flex-1">{sport.name}</span>
                  <Plus className="w-4 h-4 text-zinc-500" />
                </button>
                );
              })}
          </div>
          
          {/* Custom Sport Input */}
          <div className="pt-2 border-t border-zinc-700">
            <p className="text-xs text-zinc-400 mb-1.5">Eigene Sportart:</p>
            <div className="flex gap-2">
              <Input
                value={customSportName}
                onChange={(e) => setCustomSportName(e.target.value)}
                placeholder="z.B. Trampolinspringen"
                className="bg-zinc-900 border-zinc-700 text-white h-9 text-sm"
                onKeyDown={(e) => e.key === 'Enter' && addCustomSport()}
              />
              <Button
                onClick={addCustomSport}
                disabled={!customSportName.trim()}
                className="bg-green-500 hover:bg-green-600 text-black h-9 px-3"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Empty State */}
      {sports.length === 0 && !showAddSport && (
        <div className="text-center py-6 text-zinc-500">
          <Dumbbell className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Noch keine Sportarten hinzugefügt</p>
          <p className="text-xs mt-1">Klicke oben um deine Aktivitäten zu erfassen</p>
        </div>
      )}
      
      {/* Info Box */}
      <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
        <p className="text-xs text-blue-400 flex items-start gap-1.5">
          <Lightbulb className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span><strong>Tipp:</strong> Je genauer du deine Sportarten erfasst, desto präziser 
          wird dein Kalorienbedarf berechnet. Die KI berücksichtigt die Intensität 
          jeder Sportart individuell.</span>
        </p>
      </div>
    </div>
  );
};

// Export the sport database for use in calculations
export { SPORT_DATABASE, INTENSITY_LEVELS, getSportIcon };
export default StepSportsActivities;
